/* ==========================================================================
   ELVA — Above the fold
   Gallery paging, option selection, bundle selection, add-to-cart.
   No dependencies, no theme coupling.
   ========================================================================== */
(function () {
  'use strict';

  var money = function (cents, format) {
    var amount = (cents / 100).toFixed(2);
    if (!format) return '$' + amount;
    // Shopify money formats use {{amount}} style placeholders.
    return format.replace(/\{\{\s*\w+\s*\}\}/, function (match) {
      if (/amount_no_decimals/.test(match)) return String(Math.round(cents / 100));
      if (/with_comma_separator/.test(match)) return amount.replace('.', ',');
      return amount;
    });
  };

  /* ------------------------------------------------------------- gallery */

  function Gallery(root) {
    var track = root.querySelector('[data-elva-track]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-elva-dot]'));
    var thumbsRow = root.querySelector('[data-elva-thumbs]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-elva-thumb]'));
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    var current = 0;

    function setCurrent(index) {
      if (index === current) return;
      current = index;
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      // Keep the active thumbnail in view when the row overflows.
      var active = thumbs[index];
      if (thumbsRow && active) {
        thumbsRow.scrollTo({
          left: active.offsetLeft - (thumbsRow.clientWidth - active.offsetWidth) / 2,
          behavior: 'smooth'
        });
      }
    }

    function nearestIndex() {
      var mid = track.scrollLeft + track.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;
      slides.forEach(function (slide, i) {
        var center = slide.offsetLeft + slide.offsetWidth / 2;
        var dist = Math.abs(center - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    var ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          setCurrent(nearestIndex());
          ticking = false;
        });
      },
      { passive: true }
    );

    function goTo(i) {
      var slide = slides[i];
      if (!slide) return;
      track.scrollTo({
        left: slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2,
        behavior: 'smooth'
      });
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i);
      });
    });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        goTo(i);
      });
    });

    // Jump to the slide bound to a variant when the variant changes.
    root.addEventListener('elva:media', function (event) {
      var mediaId = event.detail && event.detail.mediaId;
      if (!mediaId) return;
      var target = slides.filter(function (slide) {
        return slide.getAttribute('data-media-id') === String(mediaId);
      })[0];
      if (!target) return;
      track.scrollTo({
        left: target.offsetLeft - (track.clientWidth - target.offsetWidth) / 2,
        behavior: 'smooth'
      });
    });
  }

  /* --------------------------------------------------------------- hero */

  function Hero(root) {
    var variants = [];
    var dataEl = root.querySelector('[data-elva-variants]');
    if (dataEl) {
      try {
        variants = JSON.parse(dataEl.textContent) || [];
      } catch (e) {
        variants = [];
      }
    }

    var form = root.querySelector('[data-elva-form]');
    var idInput = root.querySelector('[data-elva-variant-id]');
    var priceEls = Array.prototype.slice.call(root.querySelectorAll('[data-elva-price]'));
    var compareEls = Array.prototype.slice.call(
      root.querySelectorAll('[data-elva-compare]')
    );
    var buyBtn = root.querySelector('[data-elva-buy]');
    var buyLabel = root.querySelector('[data-elva-buy-label]');
    var format = root.getAttribute('data-money-format') || '';
    var soldOutText = root.getAttribute('data-sold-out') || 'Sold out';
    var buyText = buyLabel ? buyLabel.textContent : '';

    function selectedOptions() {
      var out = [];
      root.querySelectorAll('[data-elva-option]').forEach(function (group) {
        var index = parseInt(group.getAttribute('data-elva-option'), 10);
        var checked = group.querySelector('input:checked');
        out[index] = checked ? checked.value : null;
      });
      return out;
    }

    function findVariant() {
      var chosen = selectedOptions();
      if (!chosen.length) return variants[0] || null;
      return (
        variants.filter(function (variant) {
          return chosen.every(function (value, i) {
            return value == null || variant.options[i] === value;
          });
        })[0] || null
      );
    }

    function markAvailability() {
      // Grey out option values that lead to no purchasable variant.
      var chosen = selectedOptions();
      root.querySelectorAll('[data-elva-option]').forEach(function (group) {
        var index = parseInt(group.getAttribute('data-elva-option'), 10);
        group.querySelectorAll('input').forEach(function (input) {
          var probe = chosen.slice();
          probe[index] = input.value;
          var match = variants.filter(function (variant) {
            return probe.every(function (value, i) {
              return value == null || variant.options[i] === value;
            });
          })[0];
          var wrapper = input.closest('.elva-swatch, .elva-pill');
          if (!wrapper) return;
          wrapper.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    function render() {
      var variant = findVariant();

      if (idInput) idInput.value = variant ? variant.id : '';

      priceEls.forEach(function (el) {
        el.textContent = variant ? money(variant.price, format) : '';
      });

      compareEls.forEach(function (el) {
        var show =
          variant && variant.compare_at_price && variant.compare_at_price > variant.price;
        el.textContent = show ? money(variant.compare_at_price, format) : '';
        el.hidden = !show;
      });

      if (buyBtn) {
        var available = !!(variant && variant.available);
        buyBtn.disabled = !available;
        if (buyLabel) buyLabel.textContent = available ? buyText : soldOutText;
      }

      if (variant && variant.featured_media_id) {
        root.dispatchEvent(
          new CustomEvent('elva:media', {
            detail: { mediaId: variant.featured_media_id }
          })
        );
      }

      markAvailability();
    }

    root.querySelectorAll('[data-elva-option] input').forEach(function (input) {
      input.addEventListener('change', render);
    });

    /* ------------------------------------------------------------ bundles */

    var bundles = Array.prototype.slice.call(root.querySelectorAll('[data-elva-bundle]'));

    function renderBundle() {
      bundles.forEach(function (b) {
        var input = b.querySelector('input');
        b.classList.toggle('is-selected', !!(input && input.checked));
      });

      var checked = bundles.filter(function (b) {
        return b.querySelector('input').checked;
      })[0];
      if (!checked) return;

      var variantId = checked.getAttribute('data-variant-id');
      if (variantId && idInput) idInput.value = variantId;

      var price = checked.getAttribute('data-price');
      if (price) {
        priceEls.forEach(function (el) {
          el.textContent = price;
        });
      }

      var compare = checked.getAttribute('data-compare');
      compareEls.forEach(function (el) {
        el.textContent = compare || '';
        el.hidden = !compare;
      });

      // RYZE-style: the selected bundle's composite image takes over the first
      // gallery slide AND its thumbnail, then the gallery snaps back to it.
      // Swapping the slide alone left the previous bundle's picture sitting in
      // the thumbnail row, which reads as the wrong bundle the moment she
      // glances down.
      var imageUrl = checked.getAttribute('data-image');
      if (imageUrl) {
        var gallery = root.querySelector('[data-elva-track]');
        var swapImage = function (img) {
          if (!img || img.getAttribute('src') === imageUrl) return;
          // srcset/sizes would keep serving the original media otherwise.
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = imageUrl;
        };

        swapImage(gallery && gallery.querySelector('.elva-gallery__slide img'));

        var firstThumb = root.querySelector('[data-elva-thumb]');
        swapImage(firstThumb && firstThumb.querySelector('img'));

        if (gallery && gallery.scrollLeft > 0) {
          gallery.scrollTo({ left: 0, behavior: 'smooth' });
        }
      }
    }

    bundles.forEach(function (bundle) {
      var input = bundle.querySelector('input');
      if (input) input.addEventListener('change', renderBundle);
    });

    /* ---------------------------------------------------------- add to cart */

    if (form) {
      form.addEventListener('submit', function (event) {
        if (!window.fetch || form.getAttribute('data-elva-ajax') !== 'true') return;
        event.preventDefault();

        if (buyBtn) buyBtn.disabled = true;

        // Post the whole form so line item properties (upsell tick-boxes,
        // engraving fields, anything a merchant adds) survive the AJAX path.
        var payload = new FormData(form);
        payload.set('id', idInput.value);
        payload.set('quantity', '1');

        fetch(window.Shopify && window.Shopify.routes
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: payload
        })
          .then(function (response) {
            return response.json();
          })
          .then(function () {
            document.dispatchEvent(new CustomEvent('elva:added'));
            window.location.href =
              (window.Shopify && window.Shopify.routes
                ? window.Shopify.routes.root
                : '/') + 'cart';
          })
          .catch(function () {
            form.submit();
          })
          .finally(function () {
            if (buyBtn) buyBtn.disabled = false;
          });
      });
    }

    if (variants.length) render();
    if (bundles.length) renderBundle();
  }

  /* ------------------------------------------------- announcement countdown */

  function Countdown(el) {
    var deadlineAttr = el.getAttribute('data-deadline');
    var hoursAttr = parseFloat(el.getAttribute('data-hours') || '0');
    var storageKey = 'elva-countdown-' + (el.getAttribute('data-key') || 'default');

    var deadline;
    if (deadlineAttr) {
      deadline = new Date(deadlineAttr).getTime();
    } else if (hoursAttr > 0) {
      // Rolling per-visitor timer: starts on first view, persists across pages.
      var stored = null;
      try {
        stored = window.localStorage.getItem(storageKey);
      } catch (e) {
        stored = null;
      }
      var start = stored ? parseInt(stored, 10) : Date.now();
      if (!stored) {
        try {
          window.localStorage.setItem(storageKey, String(start));
        } catch (e) {
          /* private mode — timer simply restarts */
        }
      }
      deadline = start + hoursAttr * 3600 * 1000;
      if (deadline <= Date.now()) {
        deadline = Date.now() + hoursAttr * 3600 * 1000;
        try {
          window.localStorage.setItem(storageKey, String(Date.now()));
        } catch (e) {
          /* ignore */
        }
      }
    } else {
      return;
    }

    var parts = {
      hours: el.querySelector('[data-unit="hours"]'),
      minutes: el.querySelector('[data-unit="minutes"]'),
      seconds: el.querySelector('[data-unit="seconds"]')
    };

    function pad(value) {
      return value < 10 ? '0' + value : String(value);
    }

    function tick() {
      var left = Math.max(0, deadline - Date.now());
      var totalSeconds = Math.floor(left / 1000);
      var h = Math.floor(totalSeconds / 3600);
      var m = Math.floor((totalSeconds % 3600) / 60);
      var s = totalSeconds % 60;

      if (parts.hours) parts.hours.textContent = pad(h);
      if (parts.minutes) parts.minutes.textContent = pad(m);
      if (parts.seconds) parts.seconds.textContent = pad(s);

      if (left <= 0) window.clearInterval(timer);
    }

    var timer = window.setInterval(tick, 1000);
    tick();
  }

  /* ------------------------------------------------------------- lightbox */

  /* The cards are stills. One <video> serves the whole rail: the source is set
     on open and dropped on close, so a row of six testimonials costs exactly
     one download - the clip she actually taps. */
  function Reels(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-elva-reel-open]'));
    var box = root.querySelector('[data-elva-lb]');
    if (!cards.length || !box) return;

    var video = box.querySelector('[data-elva-lb-video]');
    var caption = box.querySelector('[data-elva-lb-caption]');
    var closeBtn = box.querySelector('.elva-lb__close');
    var prev = box.querySelector('[data-elva-lb-prev]');
    var next = box.querySelector('[data-elva-lb-next]');
    if (!video) return;

    var index = 0;
    var opener = null;

    if (cards.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
    }

    function show(i) {
      index = (i + cards.length) % cards.length;
      var card = cards[index];

      video.pause();
      video.setAttribute('src', card.getAttribute('data-src'));
      video.muted = false;
      if (caption) caption.textContent = card.getAttribute('data-caption') || '';

      /* A browser that refuses a sound-on start still leaves the native
         controls sitting there, so a rejected promise is not a failure. */
      var started = video.play();
      if (started && started.catch) started.catch(function () {});
    }

    function open(i, from) {
      opener = from || null;
      box.hidden = false;
      document.documentElement.classList.add('elva-lb-open');
      show(i);
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    function close() {
      video.pause();
      video.removeAttribute('src');
      video.load();            /* drops the buffer instead of leaving it resident */
      box.hidden = true;
      document.documentElement.classList.remove('elva-lb-open');
      if (opener) opener.focus({ preventScroll: true });
      opener = null;
    }

    cards.forEach(function (card, i) {
      card.addEventListener('click', function () {
        open(i, card);
      });
    });

    box.querySelectorAll('[data-elva-lb-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    document.addEventListener('keydown', function (event) {
      if (box.hidden) return;
      if (event.key === 'Escape') {
        close();
      } else if (cards.length > 1 && event.key === 'ArrowLeft') {
        show(index - 1);
      } else if (cards.length > 1 && event.key === 'ArrowRight') {
        show(index + 1);
      }
    });

    /* Swiping is how a phone expects to move between clips. */
    var startX = null;
    var startY = null;

    box.addEventListener('touchstart', function (event) {
      startX = event.changedTouches[0].clientX;
      startY = event.changedTouches[0].clientY;
    }, { passive: true });

    box.addEventListener('touchend', function (event) {
      if (startX === null || cards.length < 2) return;
      var dx = event.changedTouches[0].clientX - startX;
      var dy = event.changedTouches[0].clientY - startY;
      startX = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        show(index + (dx < 0 ? 1 : -1));
      }
    }, { passive: true });
  }

  /* ------------------------------------------------------------------ faq */

  function Faq(root) {
    root.querySelectorAll('[data-elva-qa]').forEach(function (group) {
      if (group.getAttribute('data-elva-qa') !== 'single') return;

      var items = Array.prototype.slice.call(group.querySelectorAll('details'));
      items.forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* ----------------------------------------------------------------- init */

  function init(scope) {
    (scope || document).querySelectorAll('[data-elva-hero]').forEach(function (el) {
      if (el.dataset.elvaReady) return;
      el.dataset.elvaReady = '1';
      Gallery(el);
      Hero(el);
      Reels(el);
      Faq(el);
    });

    (scope || document).querySelectorAll('[data-elva-countdown]').forEach(function (el) {
      if (el.dataset.elvaReady) return;
      el.dataset.elvaReady = '1';
      Countdown(el);
    });

    (scope || document).querySelectorAll('[data-elva-dismiss]').forEach(function (el) {
      if (el.dataset.elvaReady) return;
      el.dataset.elvaReady = '1';
      el.addEventListener('click', function () {
        var target = el.closest('[data-elva-bar]');
        if (target) target.hidden = true;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-init when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
